import React, { useMemo } from 'react';

// Components
import { Hero, TextError } from 'components/core/typo';
import classnames from 'classnames';
import { Link } from 'react-router-dom';

// Hooks
import { useTranslation } from 'react-i18next';
import { Button } from 'components/core/button';

// Icons
import { HiCheckCircle as CompletedIcon } from 'react-icons/hi2';
import { HiExclamationCircle as IncompletedIcon } from 'react-icons/hi2';
import { HiArrowsRightLeft as PendingIcon } from 'react-icons/hi2';
import { FiClock as ClockIcon } from 'react-icons/fi';
import { FiUser as InfoIcon } from 'react-icons/fi';
import { FiCalendar as CalendarIcon } from 'react-icons/fi';
import { HiOutlineBookOpen as BookIcon} from 'react-icons/hi';
import { PiGraduationCapDuotone as AcademicCapIcon} from 'react-icons/pi';
import { HiOutlineUserGroup as UserGroupIcon} from 'react-icons/hi';
import { FaBalanceScaleLeft as BalanceScaleIcon} from 'react-icons/fa';
import { FormationNavigation } from '../formation/navigation';
import { RendezVousPostFormationNavigation } from '../formation/navigation';

function StatusElement({title, description, disabled, isCompleted, children, isPending, to, href, Icon, className, doNotShowButton, ...props}){
    const {t} = useTranslation("common");
    const Component = (!disabled && (to || href)) ? Link : 'div';
    return <div className={classnames('border rounded-md p-3', 
        isPending?'bg-orange-100 border-0': isCompleted? "bg-gray-100 border-0": "border-yellow-500 border-2", 
        disabled && "!border-red-500 border-2",
        (!to && !href) && "cursor-default", className)}>
            <Component to={to || href || ""}  {...props} target={to || href ? "_blank" : undefined}>
                {disabled? <p className='flex items-center justify-end  text-red-600'>{t("tutor-inscription-status.disabled")}<IncompletedIcon className='ml-2 text-2xl'/></p>:
                isPending? 
                <p className='flex items-center justify-end  text-orange-600'>
                    {t("tutor-inscription-status.pending")}<PendingIcon className='ml-2 text-2xl'/>
                </p>:
                    isCompleted? 
                    <p className='flex items-center justify-end  text-green-600'>
                        {t("tutor-inscription-status.completed")}<CompletedIcon className='ml-2 text-2xl'/>
                    </p>:
                <p className='flex items-center justify-end text-yellow-600'>{t("tutor-inscription-status.incompleted")}<IncompletedIcon className='ml-2 text-2xl'/></p>}
                {/* {!isPending && !isCompleted && <p className='flex items-center justify-end text-yellow-600'>{t("tutor-inscription-status.incompleted")}<IncompletedIcon className='ml-2 text-2xl'/></p>} */}
                <h3 className='flex items-center text-lg font-medium'>{Icon && <Icon className="mr-2 "/>}{title}</h3>
                <p className='text-gray-500'>{description}</p>
                {!isPending &&(to || href) && !doNotShowButton && <Button color="inlineLink" size="link" href={href} to={to} className='mt-2 text-right'>{t("tutor-inscription-status.action")}</Button>}
            </Component>
            {children}
        </div>
  
}

export function TutorInscriptionStatus({report, error }){
    const {t} = useTranslation("common");

    const availabilitesCompleted = useMemo(()=>report && report.schedule_availabilities && report.schedule_availabilities.length>0, [report]);
    const daysAvailable = useMemo(()=>{
        if (!report || !report.schedule_availabilities || report.schedule_availabilities.length===0) return;
        if (report.schedule_availabilities.length>4){
            return report.schedule_availabilities.slice(0,4)
                                .map(d=>d.from)
                                .sort((a,b)=>{
                                        const week_days = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
                                        const a_day = week_days.indexOf(a.split(' ')[0]);
                                        const b_day = week_days.indexOf(b.split(' ')[0]);
                                        if (a_day<b_day) return -1;
                                        if (a_day>b_day) return 1;
                                        return 0;
                                })
                                .join(', ') + " " + t("tutor-inscription-status.availabilities.and-more", {count: report.schedule_availabilities.length-4});
        }
        const all_avail = report.schedule_availabilities.map(d=>d.from);
        // all_avail = ["lundi 8h", "lundi 9h", "mardi 17h"]

        // Order by week day
        const order_all_avail = all_avail.sort((a,b)=>{
            const week_days = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
            const a_day = week_days.indexOf(a.split(' ')[0]);
            const b_day = week_days.indexOf(b.split(' ')[0]);
            if (a_day<b_day) return -1;
            if (a_day>b_day) return 1;
            return 0;
        });
        return order_all_avail.join(', ');
    }, [report]);

    const subjectsCompleted = useMemo(()=>report && report.subjects && report.subjects.length>0, [report]);
    const subjectsAvailable = useMemo(()=>{
        if (!report || !report.subjects || report.subjects.length===0) return;
        return report.subjects.map(d=>d.name).join(', ');
    }, [report]);

    const schoolsCompleted = useMemo(()=>report && report.schools && report.schools.length>0, [report]);
    const schoolsAvailable = useMemo(()=>{
        if (!report || !report.schools || report.schools.length===0) return;
        return report.schools.map(d=>d.name).join(', ');
    }, [report]);

    const allCompleted = useMemo(()=>(availabilitesCompleted && 
                                        subjectsCompleted &&
                                         schoolsCompleted && 
                                         report &&
                                         !!report.membership_document_received && 
                                         !!report.formation_completed && 
                                         !!report.antecedent_judiciaire_document_received
                                         && report.facebook_account
                                         ), [availabilitesCompleted, subjectsCompleted, schoolsCompleted]);

    return <div className='py-12'>
                <Hero title={allCompleted?t("tutor-inscription-status.title.completed"): t("tutor-inscription-status.title.pending")}
                        subtitle={t("tutor-inscription-status.subtitle")}
                        description={allCompleted ? t("tutor-inscription-status.description-completed") : t("tutor-inscription-status.description")}>
                {report && 
                    <div className="mx-auto mt-8 space-y-8">
                        {/* Not Completed Section */}
                        {(() => {
                            const notCompletedElements = [];
                            
                            // Check availabilities
                            if (!availabilitesCompleted) {
                                notCompletedElements.push(
                                    <StatusElement key="availabilities" title={t("tutor-inscription-status.availabilities.title")}
                                                    description={t("tutor-inscription-status.availabilities.description.incompleted")}
                                                    isCompleted={false}
                                                    to={"/settings/availabilities"}
                                                    Icon={ClockIcon}
                                                    />
                                );
                            }
                            
                            // Check subjects
                            if (!subjectsCompleted) {
                                notCompletedElements.push(
                                    <StatusElement key="subjects" title={t("tutor-inscription-status.subjects.title")}
                                                    description={t("tutor-inscription-status.subjects.description.incompleted")}
                                                    isCompleted={false}
                                                    to={"/settings/subjects"}
                                                    Icon={BookIcon}
                                                    />
                                );
                            }
                            
                            // Check schools
                            if (!schoolsCompleted) {
                                notCompletedElements.push(
                                    <StatusElement key="schools" title={t("tutor-inscription-status.schools.title")}
                                                    description={t("tutor-inscription-status.schools.description.incompleted")}
                                                    isCompleted={false}
                                                    to={"/settings/schools"}
                                                    Icon={AcademicCapIcon}
                                                    />
                                );
                            }
                            
                            // // Check membership
                            // if (!report.membership_document_received) {
                            //     notCompletedElements.push(
                            //         <StatusElement key="membership" title={t("tutor-inscription-status.membership.title")}
                            //                         description={t("tutor-inscription-status.membership.description.incompleted")}
                            //                         isCompleted={false}
                            //                         target="_blank"
                            //                         href={"https://docs.google.com/forms/d/e/1FAIpQLSfQFEaq9LulTPpAd9c4eTgxgZN3gTXi_8hfFd--1M3Gcae1jg/viewform"}
                            //                         Icon={EditIcon}
                            //                         />
                            //     );
                            // }
                            
                            // Check formation
                            if (!report.formation_completed) {
                                notCompletedElements.push(
                                    <StatusElement key="formation" title={t("tutor-inscription-status.formation.title")}
                                            description={t("tutor-inscription-status.formation.description.incompleted")}
                                            isCompleted={false}
                                            Icon={UserGroupIcon}
                                            >
                                            <FormationNavigation formationCompleted={false} formationStep={report.formation_step}/>
                                    </StatusElement>
                                );
                            }

                            // If identity_verified
                            if (!report.identity_verified) {
                                notCompletedElements.push(
                                    <StatusElement key="identity" title={t("tutor-inscription-status.rdv-post-formation.title")}
                                                    description={t("tutor-inscription-status.rdv-post-formation.description.incompleted")}
                                                    isCompleted={false}
                                                    Icon={CalendarIcon} 
                                                    disabled={!report.formation_completed}
                                                    doNotShowButton={true}
                                                    href={"https://calendly.com/tutoratvirtuel-diplomeavantlamedaille/rencontre-de-bienvenue-tuteurs-benevoles-dam"}
                                                    >
                                                    <RendezVousPostFormationNavigation completed={false} disabled={!report.formation_completed}/>
                                                    </StatusElement>
                                );
                            }
                            
                            // Check antecedent judiciaire
                            const antecedentCompleted = report && !!report.antecedent_judiciaire_document_received;
                            const antecedentPending = report && report.antecedent_judiciaire_document_received && (!report.antecedent_judiciaire_document_conform|| !report.antecedent_judiciaire_document_validated);
                            
                            if (!antecedentCompleted || antecedentPending) {
                                const description = !report? null:
                                    (!report.antecedent_judiciaire_document_received 
                                    && !report.antecedent_judiciaire_document_conform
                                    && !report.antecedent_judiciaire_document_validated)?
                                        t("tutor-inscription-status.antecedent-judiciaire.description.incompleted"):
                                    (report.antecedent_judiciaire_document_received 
                                    && !report.antecedent_judiciaire_document_conform
                                    && !report.antecedent_judiciaire_document_validated)
                                    ?
                                    t("tutor-inscription-status.antecedent-judiciaire.description.received"):
                                    (report.antecedent_judiciaire_document_received 
                                    && report.antecedent_judiciaire_document_conform
                                    && !report.antecedent_judiciaire_document_validated)?
                                    t("tutor-inscription-status.antecedent-judiciaire.description.conform"):
                                    t("tutor-inscription-status.antecedent-judiciaire.description.incompleted");
                                    
                                notCompletedElements.push(
                                    <StatusElement key="antecedent" title={t("tutor-inscription-status.antecedent-judiciaire.title")}
                                                    description={description}
                                                    isCompleted={antecedentCompleted}
                                                    isPending={antecedentPending}
                                                    to={report&&!report.antecedent_judiciaire_document_validated &&"/documents"}
                                                    Icon={BalanceScaleIcon}
                                                    />
                                );
                            }
                            
                            // Check information
                            if (!report.facebook_account) {
                                notCompletedElements.push(
                                    <StatusElement key="information" title={t("tutor-inscription-status.information.title")}
                                            description={t("tutor-inscription-status.information.description.incompleted")}
                                                    to={"/settings/info"}
                                                    isCompleted={false}
                                            Icon={InfoIcon}
                                            />
                                );
                            }
                            
                            if (notCompletedElements.length > 0) {
                                return (
                                    <div className=''>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("tutor-inscription-status.incompleted")}</h3>
                                        <dl className="grid max-w-xl grid-cols-1 md:p-3 gap-x-8 gap-y-3 md:gap-y-8 md:max-w-none md:grid-cols-2">
                                            {notCompletedElements}
                                        </dl>
                                    </div>
                                );
                            }
                            return null;
                        })()}
                        
                        {/* Completed Section */}
                        {(() => {
                            const completedElements = [];
                            
                            // Check availabilities
                            if (availabilitesCompleted) {
                                completedElements.push(
                                    <StatusElement key="availabilities" title={t("tutor-inscription-status.availabilities.title")}
                                                    description={t("tutor-inscription-status.availabilities.description.completed", {days: daysAvailable})}
                                                    isCompleted={true}
                                                    to={"/settings/availabilities"}
                                                    Icon={ClockIcon}
                                                    />
                                );
                            }
                            
                            // Check subjects
                            if (subjectsCompleted) {
                                completedElements.push(
                                    <StatusElement key="subjects" title={t("tutor-inscription-status.subjects.title")}
                                                    description={t("tutor-inscription-status.subjects.description.completed", {subjects: subjectsAvailable})}
                                                    isCompleted={true}
                                                    to={"/settings/subjects"}
                                                    Icon={BookIcon}
                                                    />
                                );
                            }

                            // Check identity
                            if (report.identity_verified) {
                                completedElements.push(
                                    <StatusElement key="identity" title={t("tutor-inscription-status.rdv-post-formation.title")}
                                                    description={t("tutor-inscription-status.rdv-post-formation.description.completed")}
                                                    isCompleted={true}
                                                    Icon={CalendarIcon}
                                                    />
                                );
                            }
                            
                            // Check schools
                            if (schoolsCompleted) {
                                completedElements.push(
                                    <StatusElement key="schools" title={t("tutor-inscription-status.schools.title")}
                                                    description={t("tutor-inscription-status.schools.description.completed", {schools: schoolsAvailable})}
                                                    isCompleted={true}
                                                    to={"/settings/schools"}
                                                    Icon={AcademicCapIcon}
                                                    />
                                );
                            }
                            
                            // // Check membership
                            // if (report.membership_document_received) {
                            //     completedElements.push(
                            //         <StatusElement key="membership" title={t("tutor-inscription-status.membership.title")}
                            //                         description={t("tutor-inscription-status.membership.description.completed")}
                            //                         isCompleted={true}
                            //                         target="_blank"
                            //                         href={"https://docs.google.com/forms/d/e/1FAIpQLSfQFEaq9LulTPpAd9c4eTgxgZN3gTXi_8hfFd--1M3Gcae1jg/viewform"}
                            //                         Icon={EditIcon}
                            //                         />
                            //     );
                            // }
                            
                            // Check formation
                            if (report.formation_completed) {
                                completedElements.push(
                                    <StatusElement key="formation" title={t("tutor-inscription-status.formation.title")}
                                            description={t("tutor-inscription-status.formation.description.completed")}
                                            isCompleted={true}
                                            Icon={UserGroupIcon}
                                            >
                                            <FormationNavigation formationCompleted={true} formationStep={report.formation_step}/>
                                    </StatusElement>
                                );
                            }
                            
                            // Check antecedent judiciaire (only if fully completed)
                            const antecedentFullyCompleted = report && report.antecedent_judiciaire_document_received && report.antecedent_judiciaire_document_conform && report.antecedent_judiciaire_document_validated;
                            if (antecedentFullyCompleted) {
                                completedElements.push(
                                    <StatusElement key="antecedent" title={t("tutor-inscription-status.antecedent-judiciaire.title")}
                                                    description={t("tutor-inscription-status.antecedent-judiciaire.description.completed")}
                                                    isCompleted={true}
                                                    to={false}
                                                    Icon={BalanceScaleIcon}
                                                    />
                                );
                            }
                            
                            // Check information
                            if (report.facebook_account) {
                                completedElements.push(
                                    <StatusElement key="information" title={t("tutor-inscription-status.information.title")}
                                            description={t("tutor-inscription-status.information.description.completed")}
                                                    to={"/settings/info"}
                                                    isCompleted={true}
                                            Icon={InfoIcon}
                                            />
                                );
                            }
                            
                            if (completedElements.length > 0) {
                                return (
                                    <div className="mx-auto pt-20">
                                        <h3 className="text-lg font-semibold text-gray-900 ">{t("tutor-inscription-status.completed-many")}</h3>
                                        <p className="text-gray-500 mb-4">{t("tutor-inscription-status.completed-section-description")}</p>
                                        <dl className="grid max-w-xl grid-cols-1 md:p-3 gap-x-8 gap-y-3 md:gap-y-8 md:max-w-none md:grid-cols-2">
                                            {completedElements}
                                        </dl>
                                    </div>
                                );
                            }
                            return null;
                        })()}
                    </div>

                    
                }
                </Hero>

                {error && <TextError {...error}/>}
            </div>
}